export type SeedId = "decision" | "stuck" | "change" | "heavy-day";
export const SEED_IDS: SeedId[] = ["decision", "stuck", "change", "heavy-day"];

export type Pair = { a: string; b: string; c?: string };
export type Answers = Record<string, string | Pair>;
export type SeenAngle = { angleId: string; content?: string; fallback?: boolean };
export type Turn = { role: "guide" | "person"; text: string };

export type FlowState = {
  flowId: string;
  seedId: SeedId | null;
  src?: string;
  c?: string;
  answers: Answers;
  seen: SeenAngle[];
  helpfulAngleId?: string;
  turns: Turn[];
  step?: { text: string; when: "today" | "week" };
  reliefLine?: string;
};

const FLOW_KEY = "start_flow_v1";
const PENDING_KEY = "start_pending_keep";
const VISITOR_KEY = "start_vid";

const randomId = (): string => {
  const c: any = (window as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export function newFlow(seedId: SeedId | null, src?: string, c?: string): FlowState {
  return { flowId: randomId(), seedId, src, c, answers: {}, seen: [], turns: [] };
}

export function loadFlow(): FlowState | null {
  try {
    const raw = sessionStorage.getItem(FLOW_KEY);
    return raw ? (JSON.parse(raw) as FlowState) : null;
  } catch {
    return null;
  }
}

export function saveFlow(f: FlowState): void {
  try {
    sessionStorage.setItem(FLOW_KEY, JSON.stringify(f));
  } catch {
    /* storage full or blocked: the flow still works in memory */
  }
}

export function clearFlow(): void {
  try {
    sessionStorage.removeItem(FLOW_KEY);
  } catch {
    /* ignore */
  }
}

export function getVisitorId(): string {
  try {
    const stored = localStorage.getItem(VISITOR_KEY);
    if (stored) return stored;
    const v = randomId();
    localStorage.setItem(VISITOR_KEY, v);
    return v;
  } catch {
    return "anonymous-visitor";
  }
}

export function normalizeLocale(x?: string): "en" | "he" | "de" {
  const base = String(x || "").slice(0, 2).toLowerCase();
  return base === "he" || base === "de" ? base : "en";
}

export function interpolate(template: string, values: Record<string, any>): string {
  return String(template || "").replace(/\{\{\s*([a-zA-Z_.]+)\s*\}\}/g, (_, key: string) => {
    const v = key.split(".").reduce<any>((o, k) => (o && typeof o === "object" ? o[k] : undefined), values);
    return v == null ? "" : String(v);
  });
}

export type CardSeedText = { title: string };

export function buildCardText(f: FlowState, seedText: CardSeedText, angleTitle: string): string {
  const situation = Object.values(f.answers)
    .map((v) => (typeof v === "string" ? v : [v.a, v.b, v.c].filter(Boolean).join(" / ")))
    .filter(Boolean)
    .join("\n");
  const helped = f.seen.find((s) => s.angleId === f.helpfulAngleId);
  return [
    seedText.title,
    situation,
    helped ? `${angleTitle}${helped.content ? `: ${helped.content}` : ""}` : "",
    f.reliefLine || "",
  ]
    .filter(Boolean)
    .join("\n\n");
}

export function markPendingKeep(): void {
  try {
    sessionStorage.setItem(PENDING_KEY, "1");
  } catch {
    /* ignore */
  }
}
export function hasPendingKeep(): boolean {
  try {
    return sessionStorage.getItem(PENDING_KEY) === "1";
  } catch {
    return false;
  }
}
export function clearPendingKeep(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
